import { Component, Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useLocation } from 'react-router'
import { isUniversalSearchEnabled } from '../config/universalSearch'
import { SearchContext } from './SearchContext'
import SearchShell from './SearchShell'
import { trackEvent } from '../lib/analytics'

const SearchDialog = lazy(() => import('./UniversalSearchDialog'))

class SearchLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  render() {
    return this.state.failed ? (
      <p role="alert" className="p-4">
        Search could not load. Close it and reload the page to retry.
      </p>
    ) : (
      this.props.children
    )
  }
}

export default function SearchProvider({ children }: { children: ReactNode }) {
  const { user, session, profile } = useAuth()
  const location = useLocation()
  const scope = `${user?.id ?? 'public'}:${session?.access_token ?? ''}:${profile?.can_access_app === false ? 'denied' : 'allowed'}`
  const [openScope, setOpenScope] = useState<string | null>(null)
  const enabled =
    isUniversalSearchEnabled() &&
    !['/inactive-feedback', '/auth/callback'].includes(location.pathname)
  const open = enabled && openScope === scope
  const close = useCallback(() => setOpenScope(null), [])
  const openSearch = useCallback(() => {
    if (!enabled || document.querySelector('[role="dialog"], dialog[open], [aria-modal="true"]'))
      return
    setOpenScope(scope)
    trackEvent('Search', 'search_opened')
  }, [enabled, scope])
  useEffect(() => {
    if (!enabled) return
    const shortcut = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.altKey ||
        event.shiftKey ||
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLowerCase() !== 'k'
      )
        return
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      if (document.querySelector('[role="dialog"], dialog[open], [aria-modal="true"]')) return
      event.preventDefault()
      openSearch()
    }
    document.addEventListener('keydown', shortcut)
    return () => document.removeEventListener('keydown', shortcut)
  }, [enabled, openSearch])
  if (!enabled) return <>{children}</>
  return (
    <SearchContext.Provider value={{ openSearch, isOpen: open }}>
      {children}
      {open && (
        <SearchShell key={scope} onClose={close}>
          <SearchLoadBoundary>
            <Suspense
              fallback={
                <p role="status" className="p-4">
                  Loading search…
                </p>
              }
            >
              <SearchDialog onClose={close} />
            </Suspense>
          </SearchLoadBoundary>
        </SearchShell>
      )}
    </SearchContext.Provider>
  )
}
